import { DEPARTMENTS } from '@certhon/models';
import { z } from 'zod';

/** Lowest zxcvbn score (0-4) the auth service accepts for a new password. */
export const MINIMUM_PASSWORD_STRENGTH = 2;

/**
 * Sentinel value for the "no department" select option (Radix Select does not
 * allow an empty-string item value). Mapped to `null` when saving.
 */
export const NO_DEPARTMENT = 'none' as const;

/**
 * Personal profile fields saved through `PATCH /me`. Mirrors the legacy
 * settings validation: a name under 30 characters without line breaks.
 */
export const profileSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(29, 'Name should have less than 30 characters')
    .refine((value) => !/\n/.test(value), 'Name may not contain line breaks'),
  department: z.enum([NO_DEPARTMENT, ...DEPARTMENTS]),
});

export type ProfileFormValues = z.infer<typeof profileSchema>;

/**
 * New-password pair, shared by the password change form and the public
 * activate / recover pages. Combine with `passwordsMatch` after extending.
 */
export const passwordFields = {
  password: z.string().min(1, 'Enter a new password'),
  repeatPassword: z.string(),
};

export const passwordsMatch = {
  check: (values: { password: string; repeatPassword: string }) =>
    values.password === values.repeatPassword,
  options: {
    message: 'Your repeated password does not match',
    path: ['repeatPassword'],
  },
};

/**
 * Password change. Strength is checked against zxcvbn in the form (the
 * library is lazy-loaded); the auth service repeats that check and answers
 * 403 "Password too weak" when it fails.
 */
export const passwordSchema = z
  .object(passwordFields)
  .refine(passwordsMatch.check, passwordsMatch.options);

export type PasswordFormValues = z.infer<typeof passwordSchema>;

/** New email address, confirmed through the verification-mail flow. */
export const emailSchema = z.object({
  email: z.string().trim().email('Enter a valid email address'),
});

export type EmailFormValues = z.infer<typeof emailSchema>;
